// src/components/AnnouncementToastManager.js
// Manages toast notifications from announcements for staff and non-customer users

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getFirestore, collection, query, where, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { matchesTargeting, trackAnnouncementView, trackAnnouncementClick, trackAnnouncementDismissal } from '../services/announcementAnalytics';
import {
    markAnnouncementDismissed,
    markAnnouncementToastShown,
    shouldShowAnnouncementToast,
    announcementShowsToast,
} from '../utils/announcementDisplayStorage';
import ToastNotification from './ToastNotification';
import './ToastNotification.css';
import { resolvePricingKeyFromUserData } from '../services/pricingTierService';

const db = getFirestore();
const MAX_TOASTS = 3;

function isAdminOrProduction(userData) {
    if (!userData || !userData.role) return false;

    const role = userData.role;
    const roles = typeof role === 'string'
        ? role.split(',').map((r) => r.trim())
        : Array.isArray(role) ? role : [role];

    return roles.some((r) => r === 'Admin' || r === 'Production');
}

export default function AnnouncementToastManager({ position = 'top-right' }) {
    const { user } = useAuth();
    const [userData, setUserData] = useState(null);
    const [toasts, setToasts] = useState([]);
    const [globalEnabled, setGlobalEnabled] = useState(true);
    const shownToastIdsRef = useRef(new Set());

    useEffect(() => {
        if (!user?.uid) {
            setUserData(null);
            return;
        }

        const fetchUserData = async () => {
            try {
                const userDoc = await getDoc(doc(db, 'users', user.uid));
                if (userDoc.exists()) {
                    setUserData({ uid: user.uid, ...userDoc.data() });
                } else {
                    setUserData({ uid: user.uid, role: 'Customer', Pricing: 'a' });
                }
            } catch (error) {
                console.error('Error fetching user data for toasts:', error);
                setUserData({ uid: user.uid, role: 'Customer', Pricing: 'a' });
            }
        };

        fetchUserData();
    }, [user?.uid]);

    useEffect(() => {
        getDoc(doc(db, 'system_settings', 'announcements'))
            .then((settingsDoc) => {
                if (settingsDoc.exists() && settingsDoc.data().enabled === false) {
                    setGlobalEnabled(false);
                }
            })
            .catch((error) => {
                console.error('Error checking global settings:', error);
            });
    }, []);

    useEffect(() => {
        if (!userData || !isAdminOrProduction(userData) || !globalEnabled) {
            return;
        }

        const announcementsQuery = query(
            collection(db, 'announcements'),
            where('active', '==', true)
        );

        const unsubscribe = onSnapshot(announcementsQuery, (snapshot) => {
            const now = new Date().toISOString().split('T')[0];
            const candidates = [];

            snapshot.forEach((docSnapshot) => {
                const data = { id: docSnapshot.id, ...docSnapshot.data() };

                if (!shouldShowAnnouncementToast(data)) return;
                if (data.expiryDate && data.expiryDate < now) return;

                if (!announcementShowsToast(data)) return;

                if (!matchesTargeting(data, userData)) return;

                candidates.push({
                    id: docSnapshot.id,
                    title: data.title,
                    message: data.message,
                    type: data.toastType || 'info',
                    ctaText: data.ctaText,
                    ctaLink: data.ctaLink,
                    ctaColor: data.ctaColor || '#B99040',
                    icon: data.icon,
                    showIcon: data.showIcon !== false,
                    duration: data.toastDuration ?? 6000,
                    position: data.toastPosition || position,
                    announcementId: docSnapshot.id,
                    priority: data.priority || 1,
                    displayFrequency: data.displayFrequency || 'once',
                });
            });

            candidates.sort((a, b) => (b.priority || 1) - (a.priority || 1));
            const limitedToasts = candidates.slice(0, MAX_TOASTS);

            setToasts((prevToasts) => {
                const existingIds = new Set(prevToasts.map((t) => t.id));
                const toAdd = limitedToasts.filter(
                    (t) => !existingIds.has(t.id) && !shownToastIdsRef.current.has(t.id)
                );

                toAdd.forEach((toast) => {
                    shownToastIdsRef.current.add(toast.id);
                    markAnnouncementToastShown(toast.id);
                    trackAnnouncementView(
                        toast.announcementId,
                        userData.uid,
                        userData.role,
                        resolvePricingKeyFromUserData(userData),
                        'A'
                    ).catch((err) => console.warn('[AnnouncementToastManager] view track failed', err));
                });

                return toAdd.length ? [...prevToasts, ...toAdd] : prevToasts;
            });
        }, (error) => {
            console.error('Error listening to announcements:', error);
        });

        return () => unsubscribe();
    }, [userData, globalEnabled, position]);

    const handleDismiss = useCallback(async (toastId) => {
        const toast = toasts.find((t) => t.id === toastId);
        setToasts((prev) => prev.filter((t) => t.id !== toastId));

        let displayFrequency = toast?.displayFrequency || 'once';
        try {
            const announcementDoc = await getDoc(doc(db, 'announcements', toastId));
            if (announcementDoc.exists()) {
                displayFrequency = announcementDoc.data().displayFrequency || 'once';
            }
        } catch (error) {
            console.error('Error reading announcement for dismiss:', error);
        }

        markAnnouncementDismissed(toastId, displayFrequency);

        if (toast?.announcementId) {
            trackAnnouncementDismissal(toast.announcementId, userData?.uid, 0, 'A').catch(
                (err) => console.warn('[AnnouncementToastManager] dismissal track failed', err)
            );
        }
    }, [toasts, userData]);

    const handleCTAClick = useCallback(async (toastId, ctaLink) => {
        const toast = toasts.find((t) => t.id === toastId);
        if (toast?.announcementId) {
            trackAnnouncementClick(
                toast.announcementId,
                userData?.uid,
                userData?.role,
                ctaLink,
                'A'
            ).catch((err) => console.warn('[AnnouncementToastManager] click track failed', err));
        }
    }, [toasts, userData]);

    if (!userData || !isAdminOrProduction(userData)) {
        return null;
    }

    return (
        <div className={`toast-container toast-${position}`}>
            {toasts.map((toast) => (
                <ToastNotification
                    key={toast.id}
                    notification={toast}
                    onDismiss={handleDismiss}
                    onCTAClick={handleCTAClick}
                    position={position}
                />
            ))}
        </div>
    );
}
